<template>
  <SoRow>
    <SoCol
      v-for="(tariff, index) in filterTariffs(fuelType)"
      :key="tariff.code"
      :default-width="3"
      :lg-and-down-width="4"
      :mobile-width="4"
    >
      <SoRadioCard :value="tariff.bundleCode" data-testid="tq-tariff-card">
        <template #title>
          <div class="d-flex gap-1 align-center">
            <div class="tariff-card__title">{{ tariff.display }}</div>
            <v-spacer />
            <template
              v-if="tariff.code === cheapestTariffCode(filterTariffs(fuelType))"
            >
              <SoChip text="Lowest Cost &#10003;" :type="SoChipType.SUCCESS" />
            </template>
            <SoChip
              v-if="tariff.isNSC"
              text="NSC"
              :type="SoChipType.IMPORTANT"
            />
          </div>
        </template>
        <div class="tariff-card__grid">
          <div>Annual Cost</div>
          <div>
            {{
              formatMoney({
                amount: tariffHelper.calculateBasicAnnualCost(
                  tariff,
                  internalUsage,
                ),
              })
            }}
          </div>
          <div>Rate</div>
          <div>{{ tariff.unitRatesFormatted }}</div>
          <div>Standing Charge</div>
          <div>
            {{
              formatPriceInPence(tariff.productDetails.standingCharge ?? 0)
            }}/day
          </div>
          <div>Duration</div>
          <div>
            {{
              tariff.durationMonths ? `${tariff.durationMonths} months` : "N/A"
            }}
          </div>
        </div>
        <v-expand-transition>
          <div v-show="showDetails[index]">
            <div class="tariff-card__grid mt-2">
              <div>Public</div>
              <div>
                {{ tariff.isAvailableToPublic }}
              </div>
              <div>Prepay</div>
              <div>
                {{ tariff.isPrepay }}
              </div>
              <div>Smart Prepay</div>
              <div>
                {{ tariff.isSmartPrepay }}
              </div>
              <div>Direct Debit</div>
              <div>
                {{ tariff.directDebit }}
              </div>
            </div>
          </div>
        </v-expand-transition>
        <SoLink
          :text="showDetails[index] ? 'Hide Details' : 'See more'"
          :icon="showDetails[index] ? 'chevron-up' : 'chevron-down'"
          class="mt-2"
          @click="showDetails[index] = !showDetails[index]"
        />
      </SoRadioCard>
    </SoCol>
  </SoRow>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import { useTariffQuoter, FuelType } from "@/stores/tariffQuoter"
  import { formatMoney, formatPriceInPence } from "@/helpers/moneyHelpers"
  import { ITariffBundle } from "@/models/tariffBundle/tariffBundle.types"
  import { ITariff } from "@/models/tariff/tariff.types"
  import { IUsageEstimation } from "@/services/UsageEstimationService/types"
  import tariffHelper from "@/helpers/tariffHelper"
  import SoRow from "@/components/SoRow.vue"
  import SoCol from "@/components/SoCol.vue"
  import SoRadioCard from "@/components/SoRadioCard.vue"
  import SoChip from "@/components/SoChip.vue"
  import SoLink from "@/components/SoLink.vue"
  import { SoChipType } from "@/types/components/SoChip"

  export default defineComponent({
    components: {
      SoRow,
      SoCol,
      SoRadioCard,
      SoChip,
      SoLink,
    },
    props: {
      fuelType: {
        type: String as PropType<FuelType>,
        required: true,
      },
      usage: {
        type: Object as PropType<Partial<IUsageEstimation>>,
        default: undefined,
      },
    },
    data() {
      return {
        tariffQuoter: useTariffQuoter(),
        formatMoney,
        formatPriceInPence,
        SoChipType,
        tariffHelper,
        showDetails: [] as boolean[],
      }
    },
    computed: {
      internalUsage() {
        return this.usage ?? this.tariffQuoter.selectedUsage
      },
    },
    methods: {
      filterTariffs(type: FuelType): ITariff[] {
        const bundles: ITariffBundle[] = [...this.tariffQuoter.filteredBundles]
        let tariffs: (ITariff | undefined)[] = []

        if (type === "gas") {
          tariffs = bundles.map((bundle) => bundle.gas)
        } else if (
          this.internalUsage.electricityDay ||
          this.internalUsage.electricityNight
        ) {
          tariffs = bundles.map(
            (bundle) => bundle.elec2Rate ?? bundle.elec1Rate,
          )
        } else {
          tariffs = bundles.map((bundle) => bundle.elec1Rate)
        }

        const validTariffs: ITariff[] = tariffs.filter(
          (tariff) => tariff !== undefined,
        ) as ITariff[]

        return validTariffs
      },
      cheapestTariffCode(tariffs: ITariff[]) {
        return [...tariffs].sort((a, b) =>
          tariffHelper.calculateBasicAnnualCost(a, this.internalUsage) >
          tariffHelper.calculateBasicAnnualCost(b, this.internalUsage)
            ? 1
            : -1,
        )?.[0].code
      },
    },
  })
</script>

<style scoped>
  .tariff-card__title {
    white-space: pre-line;
    display: inline-block;
    overflow-wrap: break-word;
  }
  .tariff-card__grid {
    display: grid;
    grid-template-columns: 8fr 12fr;
    gap: 8px;
  }
</style>
